/**
 * How a row is measured for `@tanstack/react-virtual` — the height the
 * virtualizer compares against its own cached size.
 *
 * **Sub-pixel noise is HELD, not rounded away, and the difference matters
 * (PSTUDIO-4119).** The virtualizer feeds this number to `resizeItem`, which
 * compares it against the cached size with a STRICT `delta !== 0` and, on any
 * difference, writes the new size and calls `notify` → React re-render → the
 * row is measured again. A raw `getBoundingClientRect().height` is fractional,
 * so a value that flips between repaints never lets that cycle close: it ran
 * until React hit its nested-update limit and threw #185 (Maximum update depth
 * exceeded), which the app-wide boundary turned into the "Reload Page" screen
 * for the whole Workflows route.
 *
 * Rounding does NOT close that class, and this was measured rather than
 * reasoned — each rule merely moves the dead zone to its own boundary:
 *
 * | flip               | `Math.ceil` | `Math.round` | hysteresis |
 * | ------------------ | ----------- | ------------ | ---------- |
 * | 68.4 ↔ 68.6        | settles     | LOOPS        | settles    |
 * | 31.9998 ↔ 32.0002  | LOOPS       | settles      | settles    |
 * | 31.2 ↔ 32.0        | settles     | LOOPS        | settles    |
 *
 * Both flip shapes are real here: a row's height is a whole number by default
 * (`--c-editor-table-tr-height`, falling back to `32px` in
 * `table/src/draggable-row.scss`), which is the worst case for `ceil`, and a
 * mouse resize writes a FRACTIONAL height into that same variable
 * (`table.tsx`, `e.clientY - resizeRowInitialTopRef.current`), which is the
 * worst case for `round`.
 *
 * **Scope of the guarantee, stated exactly.** Noise STRICTLY BELOW
 * `HYSTERESIS_PX` is absorbed for any pair of values, wherever the integer
 * boundaries fall. A flip whose amplitude is >= 1px is NOT absorbed and cannot
 * be: a damper that swallowed it would swallow a real resize too. So for the
 * table this is a damper, not a prohibition — the structural fix is the one the
 * validation popover took, which is to stop measuring at all. The convergence
 * test drives every row of the table above, both rounding rules, and a >= 1px
 * flip, so all of this is checked rather than asserted here.
 *
 * The comparison is against the last RAW reading, not against the rounded value
 * that was reported for it: comparing raw-to-rounded widens the dead zone to
 * 1.5px whenever the previous value rounded up, which silently swallows a real
 * one-pixel resize (measured: 5 of 10 starting fractions did exactly that).
 */
const HYSTERESIS_PX = 1;
/** Slack for the floating-point error in a subtraction of two pixel values.
 *  Far below any real measurement difference, far above a double's ULP here. */
const EPSILON_PX = 1e-6;

/** The last raw reading for an element, and what was reported for it. Keyed by
 *  node, so two rows never damp each other, and entries die with the elements
 *  (`WeakMap`). */
const heldHeights = new WeakMap<Element, { raw: number; reported: number }>();

/**
 * Read the box, preferring the size the `ResizeObserver` already computed.
 *
 * Reading `entry.borderBoxSize` avoids forcing a synchronous layout once per
 * observed row, and matches what the observer actually saw. The fallback is
 * `getBoundingClientRect().height` and NOT the integer `offsetHeight`, which
 * would look tidier and be wrong: both readings feed the same held anchor, so
 * mixing an integer source with a fractional one re-creates the asymmetry this
 * module exists to remove. Measured: with `offsetHeight` on the ref path, a row
 * whose real height is 32.6 anchors at 33, and a genuine 1px growth to 33.6 is
 * then swallowed — 9948 of 20000 random fractional heights lost their resize
 * that way. Both sources must be fractional, or neither.
 *
 * The ref path (`Virtualizer.measureElement`, called on every mount) passes no
 * entry, so this fallback is a hot path, not an edge case.
 */
const rawHeight = (el: Element, entry?: ResizeObserverEntry): number => {
  const box = entry?.borderBoxSize?.[0];
  return box
    ? box.blockSize
    : (el as HTMLElement).getBoundingClientRect().height;
};

export const measureElementHeight = (
  el: Element,
  entry?: ResizeObserverEntry
): number => {
  const raw = rawHeight(el, entry);

  // A non-finite reading must never reach the virtualizer: `delta !== 0` is
  // true for NaN on every pass, so it would poison `itemSizeCache` and recreate
  // the very loop this module exists to prevent.
  if (!Number.isFinite(raw)) {
    return heldHeights.get(el)?.reported ?? 0;
  }

  const held = heldHeights.get(el);
  // `- EPSILON_PX` because floating point makes a nominal 1px step come out
  // just under it: `32.3 - 31.3` is 0.9999999999999964, which would otherwise
  // be swallowed as noise (measured: 4 of 2000 starting fractions).
  if (held && Math.abs(raw - held.raw) < HYSTERESIS_PX - EPSILON_PX) {
    return held.reported;
  }

  const reported = Math.round(raw);
  heldHeights.set(el, { raw, reported });
  return reported;
};

/**
 * The `measureElement` for a virtualizer that MEASURES its rows.
 *
 * There is exactly one such consumer: the workflows table. The validation
 * popover deliberately passes none and attaches no measuring ref, which is what
 * makes its fix structural rather than damped.
 *
 * **There is deliberately no Firefox branch any more.** One was inherited from
 * PSTUDIO-2406, where the workflows table copied the guard in the library's own
 * dynamic-size example verbatim — no comment, no local measurement, and the
 * PSTUDIO-2406 spike doc never mentions Firefox. A later refactor described it
 * as "Firefox is left to its estimate", which is simply not what happens:
 * `virtual-core`'s `setOptions` DELETES every `undefined` key before spreading
 * its defaults, so passing `undefined` hands the row to the library's own
 * measurer (`Math.round(borderBoxSize.blockSize)`, falling back to the integer
 * `offsetHeight`).
 *
 * That is the whole reason the branch is gone: this function reads the same box
 * as that default and rounds it the same way, so on Firefox the branch was not
 * adding a safeguard — it was only removing the hysteresis, leaving that one
 * browser with the `round` column of the table above (measured: it loops on two
 * of the three flips). Keeping a browser opted out of the fix needs a reason,
 * and there is none on record.
 *
 * One deliberate narrowing versus the library default: it branches on
 * `options.horizontal` and reads `inlineSize`/`offsetWidth` in that mode, while
 * this always reads the block axis. Both virtualizers here are vertical, and a
 * horizontal one would need this revisited rather than silently mis-measured.
 *
 * Observation, separately: it stops only when the measuring `ref` is removed.
 * `observe()` runs from inside `Virtualizer.measureElement` and, for elements
 * already cached, from `_willUpdate` when the scroll element changes — and that
 * cache is only ever filled by `measureElement`. So a row that never receives
 * the ref is never observed, which is what makes the popover's fix structural
 * rather than damped.
 */
export const virtualizerMeasureElement: (
  el: Element,
  entry?: ResizeObserverEntry
) => number = measureElementHeight;
